// Contrato de API real (PR-04, ver docs/novas/doc_back/PR-04-modulo-acordos-endpoints-e-operacao-frontend.md):
// GET   /sinister-agreements?_page=&_limit=&status=&paymentMethod=&filterField=&busca=&dataIni=&dataFim=
// GET   /sinister-agreements/:id
// PATCH /sinister-agreements/:id/status                              { status, reason? } (reason obrigatório quando status="broken")
// POST  /sinister-agreements/:id/installments/:number/reissue        { newDueDate, paymentMethod, note? }
// POST  /sinister-agreements/:id/installments/:number/manual-settle  { paymentMethod?, status?, paymentDate?, reason?, note? } (recebido 2026-07-16)
// POST  /sinister-agreements/:id/installments/:number/create-billet  gera boleto Sicoob e envia e-mail ao segurado (recebido 2026-07-16)
// GET   /sinister-agreements/:id/installments/:number/segunda-via    retorna dados atualizados do Sicoob sem alterar a parcela (recebido 2026-07-16)
// GET   /sinister-agreements/sicoob/boleto/:installmentId            retorna o PDF do boleto (recebido 2026-07-16; ainda não usado no front —
//                                                                     falta confirmar se o backend expõe um `id` por parcela em installments[])
//
// O contrato acima foi escrito ANTES dos ajustes que fizemos na tela de Acordos (setor por acordo,
// entrada/downPaymentValue, os status "paid_off"/"broken"/"preventive"/"renegotiated") — o backend
// ainda não sabe lidar com nada disso. Enquanto isso:
// - getAll/getById consomem o backend real e, por cima, aplicam uma camada de "extras locais"
//   (LOCAL_EXTRAS, em memória, reseta ao dar reload) pra sector/downPaymentValue/overrides de status
//   e de parcela que o backend não persiste ainda.
// - updateStatus tenta o PATCH real primeiro; se o backend rejeitar (400 — enum de status desconhecido),
//   cai pro override local, sem quebrar a experiência do usuário.
// - manualSettleInstallment agora usa o endpoint real (deixou de ser 100% local).
// - cancelInstallment agora usa o endpoint real (POST .../cancel, contrato confirmado 17/08/2026 em
//   frontend-boletos-acordo-sinistro-acordo.md) — deixou de ser 100% local.
// - Repasse à imobiliária (contrato confirmado 09/08/2026, ver PR-09-negociacao-repasse-imobiliaria-frontend.md
//   e MANUAL-BACKEND-ENDPOINTS-FRONTEND.md) — dois conceitos distintos, não confundir:
//   - setAgencyTransferPlan: QUANTO deve ser repassado (planejado), definido no nível do ACORDO (menu
//     Acordos > linha do acordo > "Definir Repasse à Imobiliária"), com um valor total diluído entre as
//     parcelas (equal | fast) — não faz mais parte da negociação. Preenche `installment.agencyTransferValue`.
//     POST /sinister-agreements/:id/agency-transfer-plan.
//   - registerAgencyTransfer: registro de que o repasse foi de fato EFETUADO pra uma parcela específica
//     (menu Acordos > parcela > "Registrar Repasse") — ação independente, não exige plano prévio.
//     POST /sinister-agreements/:id/installments/:number/agency-transfer.
// - getInstallments (contrato confirmado 09/08/2026, ver PR-10-controle-cobranca-listagem-parcelas-frontend.md
//   e MANUAL-BACKEND-ENDPOINTS-FRONTEND.md): GET /sinister-agreements/installments — parcelas de todos
//   os acordos já achatadas e paginadas pelo backend, usado pela tela "Controle de Cobranças". Note que
//   o item por parcela NÃO inclui o status do acordo nem installmentHistory (só agreementId) — quem
//   precisa disso (InstallmentRowMenu) busca o acordo completo sob demanda via getById.
import { AxiosError } from "axios";
import { Api } from "../axios-config";
import { extractApiErrorMessage } from "../../../utils/extractApiErrorMessage";
import { NegotiationInstallmentIntervalType, NegotiationPaymentMethod } from "./SinisterNegotiationService";
import { SinisterSector } from "../../../../pages/creditRecovery/constants/sectorOptions";

export type AgreementStatus = "on_time" | "overdue" | "paid_off" | "broken" | "preventive" | "renegotiated";
export type AgreementInstallmentStatus = "paid" | "pending" | "overdue" | "canceled";
export type InstallmentHistoryEventType = "reissue" | "manual_settlement" | "cancellation";

// "equal": divide o valor de repasse igualmente entre todas as parcelas elegíveis.
// "fast": preenche cada parcela até o limite do seu próprio valor, na ordem, até esgotar o repasse
// (ex.: repasse de 150 em 3 parcelas de 60 -> 60, 60, 30).
export type AgencyTransferDilutionType = "equal" | "fast";

export interface IAgreementValueItem {
	id: string;
	description: string;
	reference: string;
	dueDate: string;
	value: number;
}

// Registro de que o valor de repasse dessa parcela foi efetivamente devolvido para a imobiliária.
export interface IAgencyTransferRegistration {
	id: string;
	value: number;
	transferDate: string;
	// Preenchido pelo backend a partir do usuário autenticado que registrou — não é campo de formulário.
	registeredByUserName: string;
	note?: string;
	createdAt: string;
}

export interface IAgreementInstallment {
	// Id individual da parcela — usado por GET /sinister-agreements/sicoob/boleto/:installmentId
	// (endpoint de PDF do boleto, público, recebido junto com a doc de 2026-08-16). Opcional porque
	// nem toda resposta de installments[] necessariamente traz esse campo ainda — ver getBoletoPdf.
	id?: string;
	number: number;
	dueDate: string;
	value: number;
	status: AgreementInstallmentStatus;
	paidAt?: string;
	reissueCount?: number;
	cancelReason?: string;
	// Valor de repasse PLANEJADO pra essa parcela, definido via setAgencyTransferPlan (nível do acordo,
	// não da negociação). Undefined/0 = sem repasse planejado nesta parcela.
	agencyTransferValue?: number;
	// Preenchido só depois que o repasse à imobiliária foi de fato registrado (ver registerAgencyTransfer).
	agencyTransferRegistration?: IAgencyTransferRegistration;
}

export interface IInstallmentHistoryEntry {
	id: string;
	installmentNumber: number;
	type: InstallmentHistoryEventType;
	previousDueDate: string;
	newDueDate: string;
	paymentMethod?: NegotiationPaymentMethod;
	note?: string;
	createdAt: string;
	userName?: string;
}

export interface IAgreement {
	id: string;
	sinisterId: string;
	sector: SinisterSector;
	status: AgreementStatus;
	proposal: { name: string; document: string };
	client: { name: string; document?: string };
	paymentMethod: NegotiationPaymentMethod;
	installmentsCount: number;
	installmentIntervalType?: NegotiationInstallmentIntervalType;
	installmentIntervalDays?: number;
	discountPercent?: number;
	downPaymentValue?: number;
	subtotalValue: number;
	discountAmount: number;
	totalValue: number;
	dueDate: string;
	createdAt: string;
	values: IAgreementValueItem[];
	installments: IAgreementInstallment[];
	installmentHistory: IInstallmentHistoryEntry[];
	cancelReason?: string;
	canceledAt?: string;
}

export interface IAgreementTotalCount {
	data: IAgreement[];
	totalCount: number;
}

export interface IReissueInstallmentPayload {
	newDueDate: string;
	paymentMethod: NegotiationPaymentMethod;
	note?: string;
}

export interface IManualSettleInstallmentPayload {
	paymentMethod?: NegotiationPaymentMethod;
	status?: AgreementInstallmentStatus;
	paymentDate?: string;
	reason?: string;
	note?: string;
}

export interface ICancelInstallmentPayload {
	reason: string;
}

export interface IRegisterAgencyTransferPayload {
	value: number;
	transferDate: string;
	note?: string;
}

export interface ISetAgencyTransferPlanPayload {
	totalValue: number;
	dilutionType: AgencyTransferDilutionType;
}

export interface IUpdateAgreementStatusPayload {
	status: AgreementStatus;
	reason?: string;
}

// Item achatado de GET /sinister-agreements/installments — uma linha por PARCELA (não por acordo),
// usado pela tela "Controle de Cobranças" (PR-10). Ver comentário no topo do arquivo.
export interface IAgreementInstallmentFlatRow {
	agreementId: string;
	sinisterId: string;
	certificateNumber?: string;
	proposal: { name: string; document: string };
	client: { name: string };
	paymentMethod: NegotiationPaymentMethod;
	installmentsCount: number;
	installment: IAgreementInstallment;
}

export interface IAgreementInstallmentsPage {
	data: IAgreementInstallmentFlatRow[];
	total: number;
}

export interface IGetAgreementInstallmentsParams {
	page: number;
	limit: number;
	filterField?: "certificado" | "documento" | "nome";
	textSearch?: string;
	installmentStatus?: AgreementInstallmentStatus;
	// csv já no vocabulário do backend (on_time,overdue,paid_off,broken,preventive,renegotiated) — sem
	// tradução, diferente de getAll/updateStatus (ver toBackendAgreementStatus).
	agreementStatusCsv?: string;
	paymentMethod?: NegotiationPaymentMethod;
	clientName?: string;
	dateType?: "vencimento" | "pagamento";
	dataIni?: string;
	dataFim?: string;
}

// Distribui `totalValue` entre as parcelas do acordo que AINDA NÃO têm repasse efetivamente
// CONFIRMADO (agencyTransferRegistration) — repasse é uma ação manual, independente do status da
// parcela em si (paga, em aberto, vencida), mas uma vez confirmado o repasse de uma parcela ele não
// pode ser recalculado/sobrescrito por uma redefinição (ver comentário no topo do arquivo sobre plano
// vs registro). A única coisa que bloqueia o repasse por completo é o acordo inteiro estar cancelado
// ("broken"), o que já é validado antes de chegar aqui (ver setAgencyTransferPlan e
// ModalDefineAgencyTransferPlan). Usado tanto pra prévia (espelho) quanto pra persistir o plano em
// setAgencyTransferPlan — mesma conta nos dois lugares.
export const computeAgencyTransferDistribution = (
	installments: IAgreementInstallment[],
	totalValue: number,
	dilutionType: AgencyTransferDilutionType
): Map<number, number> => {
	const eligible = installments.filter((installment) => !installment.agencyTransferRegistration);
	if (totalValue <= 0 || eligible.length === 0) return new Map();

	if (dilutionType === "equal") {
		const perInstallment = totalValue / eligible.length;
		return new Map(eligible.map((installment) => [installment.number, Math.min(perInstallment, installment.value)]));
	}

	let remaining = totalValue;
	return new Map(
		eligible.map((installment) => {
			const amount = Math.min(remaining, installment.value);
			remaining = Math.max(0, remaining - amount);
			return [installment.number, amount];
		})
	);
};

// --------- Camada de extras locais (sector/entrada/status novo/parcelas) ---------

interface LocalAgreementExtras {
	sector?: SinisterSector;
	downPaymentValue?: number;
	statusOverride?: { status: AgreementStatus; reason?: string };
	installmentOverrides?: Record<
		number,
		{
			status?: AgreementInstallmentStatus;
			cancelReason?: string;
		}
	>;
	installmentHistoryExtra?: IInstallmentHistoryEntry[];
}

const LOCAL_EXTRAS = new Map<string, LocalAgreementExtras>();

const getExtras = (agreementId: string): LocalAgreementExtras => LOCAL_EXTRAS.get(agreementId) ?? {};

const setExtras = (agreementId: string, patch: Partial<LocalAgreementExtras>) => {
	LOCAL_EXTRAS.set(agreementId, { ...getExtras(agreementId), ...patch });
};

const clearStatusOverride = (agreementId: string) => {
	const extras = getExtras(agreementId);
	if (!extras.statusOverride) return;
	const { statusOverride, ...rest } = extras;
	LOCAL_EXTRAS.set(agreementId, rest);
};

const recomputeStatusFromInstallments = (installments: IAgreementInstallment[], fallbackStatus: AgreementStatus): AgreementStatus => {
	if (fallbackStatus === "broken" || fallbackStatus === "preventive" || fallbackStatus === "renegotiated") return fallbackStatus;
	const active = installments.filter((installment) => installment.status !== "canceled");
	if (active.length > 0 && active.every((installment) => installment.status === "paid")) return "paid_off";
	if (active.some((installment) => installment.status === "overdue")) return "overdue";
	return "on_time";
};

// Backend usa "active" como valor real do status "em dia" do acordo e "CANCELED" (maiúsculo, ver
// resposta do POST /agreements/:id/cancel no PR-06) pro cancelamento — normalizamos pra "on_time"/
// "broken" (nosso vocabulário já em uso em toda a tela) na entrada (leitura) e traduzimos de volta
// na saída (filtro de listagem, PATCH de status), já que o backend não reconhece esses termos.
// Sem esse mapeamento, um acordo cancelado pelo endpoint real chegava com status "CANCELED" cru:
// o chip da tela de detalhe mostrava o texto não traduzido e a caixa de "Quebra de Acordo" (que só
// aparece quando status === "broken") ficava escondida mesmo com cancelReason preenchido.
const normalizeAgreementStatus = (status: string): AgreementStatus => {
	if (status === "active") return "on_time";
	if ((status ?? "").toUpperCase() === "CANCELED") return "broken";
	return status as AgreementStatus;
};
const toBackendAgreementStatus = (status: string): string => {
	if (status === "on_time") return "active";
	if (status === "broken") return "CANCELED";
	return status;
};

const mapBackendAgreement = (raw: any, agreementId: string): IAgreement => {
	const extras = getExtras(agreementId ?? raw?.id);
	const id = raw?.id ?? agreementId;

	const installments: IAgreementInstallment[] = (raw?.installments ?? []).map((installment: IAgreementInstallment) => {
		const override = extras.installmentOverrides?.[installment.number];
		return override ? { ...installment, ...override } : installment;
	});

	let status: AgreementStatus = extras.statusOverride?.status ?? normalizeAgreementStatus(raw?.status);
	if (!extras.statusOverride && extras.installmentOverrides && Object.keys(extras.installmentOverrides).length > 0) {
		status = recomputeStatusFromInstallments(installments, status);
	}

	const isBroken = status === "broken";

	return {
		id,
		sinisterId: raw?.sinisterId,
		sector: raw?.sector ?? raw?.setor ?? extras.sector ?? "cobranca",
		status,
		proposal: raw?.proposal ?? { name: "", document: "" },
		client: raw?.client ?? { name: "" },
		paymentMethod: raw?.paymentMethod,
		installmentsCount: raw?.installmentsCount,
		installmentIntervalType: raw?.installmentIntervalType,
		installmentIntervalDays: raw?.installmentIntervalDays,
		discountPercent: raw?.discountPercent,
		downPaymentValue: raw?.downPaymentValue ?? extras.downPaymentValue,
		subtotalValue: raw?.subtotalValue,
		discountAmount: raw?.discountAmount,
		totalValue: raw?.totalValue,
		dueDate: raw?.dueDate,
		createdAt: raw?.createdAt,
		values: raw?.values ?? [],
		installments,
		installmentHistory: [...(raw?.installmentHistory ?? []), ...(extras.installmentHistoryExtra ?? [])],
		cancelReason: isBroken ? extras.statusOverride?.reason ?? raw?.cancelReason : raw?.cancelReason,
		canceledAt: isBroken ? raw?.canceledAt ?? (extras.statusOverride ? new Date().toISOString() : undefined) : raw?.canceledAt,
	};
};

const getAll = async (
	page: number,
	limit: number,
	statusCsv?: string,
	paymentMethod?: string,
	filterField?: "documento" | "nome",
	textSearch?: string,
	dataIni?: string,
	dataFim?: string,
	sector?: SinisterSector
): Promise<IAgreementTotalCount | Error> => {
	try {
		const statusList = (statusCsv || "").split(",").filter(Boolean);
		const effectiveStatus =
			statusList.length > 0 && !statusList.includes("Todos") ? statusList.map(toBackendAgreementStatus).join(",") : "";

		const params = new URLSearchParams();
		params.set("_page", String(page));
		params.set("_limit", String(limit));
		if (effectiveStatus) params.set("status", effectiveStatus);
		if (paymentMethod && paymentMethod !== "Todos") params.set("paymentMethod", paymentMethod);
		if (filterField) params.set("filterField", filterField);
		if (textSearch) params.set("busca", textSearch);
		if (dataIni) params.set("dataIni", dataIni);
		if (dataFim) params.set("dataFim", dataFim);
		// Filtro por setor (aba Cobrança/Jurídico) — mandado otimista, igual outros parâmetros desse
		// service; ainda não confirmado se o backend de fato filtra `GET /sinister-agreements` por isso.
		if (sector) params.set("sector", sector);

		const { data } = await Api().get(`/sinister-agreements?${params.toString()}`);
		const rawList: any[] = data?.data ?? [];
		const filteredList = sector ? rawList.filter((raw) => (raw?.sector ?? raw?.setor ?? "cobranca") === sector) : rawList;

		return {
			data: filteredList.map((raw: any) => mapBackendAgreement(raw, raw.id)),
			// Se o backend já filtra por sector, total bate certinho. Se não filtra (ainda não confirmado),
			// esse total reflete os DOIS setores, então a paginação fica imprecisa até isso ser confirmado.
			totalCount: data?.total ?? filteredList.length,
		};
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao listar acordos.");
	}
};

const getById = async (id: string): Promise<IAgreement | Error> => {
	try {
		const { data } = await Api().get(`/sinister-agreements/${id}`);
		if (!data) return new Error("Acordo não encontrado.");
		return mapBackendAgreement(data, id);
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao buscar acordo.");
	}
};

const updateStatus = async (id: string, payload: IUpdateAgreementStatusPayload): Promise<IAgreement | Error> => {
	try {
		const { data } = await Api().patch(`/sinister-agreements/${id}/status`, { ...payload, status: toBackendAgreementStatus(payload.status) });
		clearStatusOverride(id);
		return mapBackendAgreement(data, id);
	} catch (error) {
		if (error instanceof AxiosError && error.response?.status === 400) {
			// Backend ainda não conhece esse status (um dos 4 novos) — mantém local até ele suportar.
			setExtras(id, { statusOverride: { status: payload.status, reason: payload.reason } });
			return getById(id);
		}
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao atualizar status do acordo.");
	}
};

const reissueInstallment = async (
	agreementId: string,
	installmentNumber: number,
	payload: IReissueInstallmentPayload
): Promise<IAgreement | Error> => {
	try {
		const { data } = await Api().post(`/sinister-agreements/${agreementId}/installments/${installmentNumber}/reissue`, payload);
		return mapBackendAgreement(data, agreementId);
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao gerar nova via da parcela.");
	}
};

const manualSettleInstallment = async (
	agreementId: string,
	installmentNumber: number,
	payload: IManualSettleInstallmentPayload
): Promise<IAgreement | Error> => {
	try {
		const { data } = await Api().post(`/sinister-agreements/${agreementId}/installments/${installmentNumber}/manual-settle`, {
			status: "paid",
			...payload,
		});
		return mapBackendAgreement(data, agreementId);
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao dar baixa manual na parcela.");
	}
};

const createBillet = async (agreementId: string, installmentNumber: number): Promise<{ message?: string } | Error> => {
	try {
		const { data } = await Api().post(`/sinister-agreements/${agreementId}/installments/${installmentNumber}/create-billet`);
		return data ?? {};
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao gerar e enviar o boleto.");
	}
};

const getSegundaVia = async (agreementId: string, installmentNumber: number): Promise<Record<string, unknown> | Error> => {
	try {
		const { data } = await Api().get(`/sinister-agreements/${agreementId}/installments/${installmentNumber}/segunda-via`);
		return data ?? {};
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao consultar a 2ª via do boleto.");
	}
};

// GET /sinister-agreements/sicoob/boleto/:installmentId — retorna o PDF do boleto da parcela (rota
// pública do Sicoob, recebida em 2026-08-16). Exige `installment.id` — parcelas sem esse campo ainda
// não têm PDF disponível pelo front (ver comentário em IAgreementInstallment).
const getBoletoPdf = async (installmentId: string): Promise<void | Error> => {
	try {
		const { data } = await Api().get(`/sinister-agreements/sicoob/boleto/${installmentId}`, { responseType: "blob" });
		if (!data) return new Error("Boleto não encontrado para essa parcela.");
		const file = new Blob([data], { type: "application/pdf" });
		const fileURL = URL.createObjectURL(file);
		window.open(fileURL);
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao abrir o PDF do boleto.");
	}
};

const cancelInstallment = async (
	agreementId: string,
	installmentNumber: number,
	payload: ICancelInstallmentPayload
): Promise<IAgreement | Error> => {
	try {
		const { data } = await Api().post(`/sinister-agreements/${agreementId}/installments/${installmentNumber}/cancel`, {
			note: payload.reason,
		});
		return mapBackendAgreement(data, agreementId);
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao cancelar a parcela.");
	}
};

const registerAgencyTransfer = async (
	agreementId: string,
	installmentNumber: number,
	payload: IRegisterAgencyTransferPayload
): Promise<IAgreement | Error> => {
	try {
		const { data } = await Api().post(`/sinister-agreements/${agreementId}/installments/${installmentNumber}/agency-transfer`, payload);
		return mapBackendAgreement(data, agreementId);
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao registrar o repasse para a imobiliária.");
	}
};

const setAgencyTransferPlan = async (agreementId: string, payload: ISetAgencyTransferPlanPayload): Promise<IAgreement | Error> => {
	try {
		const current = await getById(agreementId);
		if (current instanceof Error) return current;

		if (current.status === "broken") return new Error("Não é possível definir repasse para um acordo cancelado.");

		// Defesa extra além do clamp já feito na modal — o repasse nunca pode superar o valor total do
		// acordo, seja lá quem chamar esse método. O backend capa de novo do lado dele.
		const cappedTotalValue = Math.min(payload.totalValue, current.totalValue);
		const distribution = computeAgencyTransferDistribution(current.installments, cappedTotalValue, payload.dilutionType);
		if (distribution.size === 0) return new Error("Todas as parcelas do acordo já têm repasse confirmado — não há o que redefinir.");

		const { data } = await Api().post(`/sinister-agreements/${agreementId}/agency-transfer-plan`, {
			totalValue: cappedTotalValue,
			dilutionType: payload.dilutionType,
		});
		return mapBackendAgreement(data, agreementId);
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao definir o repasse para a imobiliária.");
	}
};

const getInstallments = async (params: IGetAgreementInstallmentsParams): Promise<IAgreementInstallmentsPage | Error> => {
	try {
		const query = new URLSearchParams();
		query.set("_page", String(params.page));
		query.set("_limit", String(params.limit));

		const cleanedSearch = (params.textSearch ?? "").trim();
		if (cleanedSearch) {
			if (params.filterField === "certificado") query.set("certificateNumber", cleanedSearch);
			else if (params.filterField === "nome") query.set("name", cleanedSearch);
			else query.set("document", cleanedSearch.replace(/\D/g, ""));
		}

		if (params.installmentStatus) query.set("installmentStatus", params.installmentStatus);
		if (params.agreementStatusCsv) query.set("agreementStatus", params.agreementStatusCsv);
		if (params.paymentMethod) query.set("paymentMethod", params.paymentMethod);
		if (params.clientName) query.set("clientName", params.clientName);
		if (params.dateType) query.set("dateType", params.dateType);
		if (params.dataIni) query.set("dataIni", params.dataIni);
		if (params.dataFim) query.set("dataFim", params.dataFim);

		const { data } = await Api().get(`/sinister-agreements/installments?${query.toString()}`);
		return { data: data?.data ?? [], total: data?.total ?? 0 };
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao listar parcelas dos acordos.");
	}
};

// GET /sinister-agreements/sicoob/conciliation?dataini=&datafim= — força a conciliação manual dos
// boletos de parcela junto ao Sicoob nesse intervalo. Serve de contingência quando o webhook
// (/sinister-agreements/... não documentado no front, é chamado direto pelo banco) falha em atualizar
// o status da parcela automaticamente. Formato de resposta ainda não tem contrato fechado — trafega
// como retorno cru, igual à 2ª via (ver getSegundaVia).
const getConciliation = async (dataIni: string, dataFim: string): Promise<any | Error> => {
	try {
		const { data } = await Api().get(`/sinister-agreements/sicoob/conciliation?dataini=${dataIni}&datafim=${dataFim}`);
		return data ?? {};
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao sincronizar os boletos com o Sicoob.");
	}
};

export const SinisterAgreementService = {
	getAll,
	getById,
	updateStatus,
	reissueInstallment,
	manualSettleInstallment,
	cancelInstallment,
	createBillet,
	getSegundaVia,
	getBoletoPdf,
	registerAgencyTransfer,
	setAgencyTransferPlan,
	getInstallments,
	getConciliation,
};
